import { APP_ROUTES } from "@/common/urls"
import GeneralLayout from "@/layouts/GeneralLayout"
import { Navigate, createBrowserRouter } from "react-router-dom"
import ProtectedRoute from "@/components/HOC/ProtectedRoutes"
import AppRouteContainer from "./containers/AppRouteContainer"
import AuthRoutesProtector from "@/components/HOC/AuthRouteProtector"
import AuthRouteContainer from "./containers/AuthRouteContainer"
import AuthLogin from "@/pages/AuthLogin"
import AppDashboard from "@/pages/AppDashboard"
import { Account } from "@/pages/Account"
import AuthLogout from "@/pages/AuthLogout"
import { CreateApplication } from "@/pages/CreateApplication"
import AuthRegister from "@/pages/AuthRegister"
import AuthForgotPasswordForm from "@/pages/AuthForgotPassword"
import { useAuth } from "@/hooks/useAuth"
import { Skeleton } from "@/components/ui/skeleton"
import AuthResetPassword from "@/pages/AuthResetPassword"
import { AccountTransferOwnership } from "@/pages/AccountTransferOwnership"

import AuthRecovery, { ConfirmRecovery } from "@/pages/AuthRecovery"
import { recoveryPath, confirmRecoveryPath } from "@/api/recovery"

export const browserRouter = createBrowserRouter([
    {
        path: APP_ROUTES.ROOT.ROOT,
        element: <GeneralLayout />,
        children: [
            { path: recoveryPath, element: <AuthRecovery /> },
            { path: confirmRecoveryPath, element: <ConfirmRecovery /> },
            {
                path: "/login",
                element: <Navigate to={APP_ROUTES.AUTH.LOGIN} replace />,
            },
            {
                path: "/register",
                element: <Navigate to={APP_ROUTES.AUTH.REGISTER} replace />,
            },
            // Root index redirect: if authenticated go to dashboard; otherwise go to login
            {
                index: true,
                element: <RootIndexRedirect />,
            },
            {
                path: APP_ROUTES.ROOT.ROOT,
                element: (
                    <ProtectedRoute>
                        <AppRouteContainer />
                    </ProtectedRoute>
                ),
                children: [
                    {
                        index: true,
                        element: (
                            <Navigate to={APP_ROUTES.ROOT.DASHBOARD} replace />
                        ),
                    },
                    {
                        path: APP_ROUTES.ROOT.ACCOUNT,
                        element: <Account />,
                    },
                    {
                        path: APP_ROUTES.ROOT.DASHBOARD,
                        element: <AppDashboard />,
                    },
                    {
                        path: APP_ROUTES.ROOT.APPLICATION,
                        element: <CreateApplication />,
                    },
                    {
                        path: APP_ROUTES.ROOT.TRANSFER_OWNERSHIP,
                        element: <AccountTransferOwnership />,
                    },
                ],
            },
            {
                path: APP_ROUTES.AUTH.INDEX,
                element: (
                    <AuthRoutesProtector>
                        <AuthRouteContainer />
                    </AuthRoutesProtector>
                ),
                children: [
                    {
                        index: true,
                        element: <Navigate to={APP_ROUTES.AUTH.LOGIN} />,
                    },
                    {
                        path: APP_ROUTES.AUTH.LOGIN,
                        element: <AuthLogin />,
                    },
                    {
                        path: APP_ROUTES.AUTH.REGISTER,
                        element: <AuthRegister />,
                    },
                    {
                        path: APP_ROUTES.AUTH.FORGOT_PASSWORD,
                        element: <AuthForgotPasswordForm />,
                    },
                    {
                        path: APP_ROUTES.AUTH.LOGOUT,
                        element: <AuthLogout />,
                    },
                    {
                        path: APP_ROUTES.AUTH.RESET_PASSWORD,
                        element: <AuthResetPassword />,
                    },
                ],
            },
        ],
    },
])

function RootIndexRedirect() {
    const { user, loading } = useAuth()
    if (loading) return <Skeleton className="w-full h-[50vh]" />
    if (user) return <Navigate to={APP_ROUTES.ROOT.DASHBOARD} replace />
    return <Navigate to={APP_ROUTES.AUTH.LOGIN} replace />
}
